import type { AssetsTranslations } from './types'

/**
 * Urdu translations for Assets/Fleet domain.
 */
export const urTranslations: AssetsTranslations = {
  // Page/Section level
  title: 'فلیٹ مینجمنٹ',
  subtitle: 'ڈرائیوروں، گاڑیوں اور وسائل کی تفویض کا انتظام',

  // Stats
  totalAssets: 'کل اثاثے',
  total: 'کل',
  active: 'فعال',
  maintenance: 'دیکھ بھال میں',
  idle: 'بے کار',
  inMaintenance: 'دیکھ بھال میں',

  // Ownership types
  owned: 'ملکیتی',
  leased: 'لیز پر',
  subcontracted: 'ذیلی معاہدہ',
  rented: 'کرایے پر',

  // Search and filters
  searchPlaceholder: 'اثاثے تلاش کریں...',
  noAssets: 'کوئی اثاثے نہیں ملے',
  noItemsFound: 'کوئی اثاثے نہیں ملے',
  noItemsDescription: 'آپ نے ابھی تک کوئی اثاثہ شامل نہیں کیا۔ شروع کرنے کے لیے اپنا پہلا اثاثہ شامل کریں۔',
  addFirst: 'اپنا پہلا اثاثہ شامل کریں',

  // Asset details
  assetDetails: 'اثاثے کی تفصیلات',
  assetId: 'اثاثے کی شناخت',
  plateNumber: 'پلیٹ نمبر',
  capacity: 'گنجائش',
  viewHistory: 'تاریخ دیکھیں',
  trackLocation: 'مقام ٹریک کریں',

  // Subcontracted details
  subcontractedDetails: 'ذیلی معاہدے کی تفصیلات',
  provider: 'فراہم کنندہ',
  contractEnd: 'معاہدے کا اختتام',
  monthlyCost: 'ماہانہ لاگت',
  contactPerson: 'رابطہ شخص',
  contactPhone: 'رابطہ فون',

  // Location info
  locationInfo: 'مقام کی معلومات',
  currentLocation: 'موجودہ مقام',
  lastUsed: 'آخری استعمال',

  // Vehicle types
  vehicleType: 'گاڑی کی قسم',
  mixer: 'مکسر',
  flatbed: 'فلیٹ بیڈ',

  // Resource management
  resourceManagement: 'وسائل کا انتظام',
  searchResources: 'وسائل تلاش کریں...',
  createNewResource: 'نیا وسیلہ بنائیں',
  createNewResourceHint: 'ڈرائیور اور گاڑی منتخب یا بنائیں',
  noMatchingResources: 'کوئی مماثل وسائل نہیں',
  noResourcesFound: 'کوئی وسائل نہیں ملے',
  createResource: 'وسیلہ بنائیں',
  resourceName: 'وسیلے کا نام',
  resourceNamePlaceholder: 'وسیلے کا نام درج کریں',
  assignedResource: 'تفویض شدہ وسیلہ',
  driverValidation: 'ڈرائیور کا نام، فون، اور شناخت ضروری ہیں',
  plateValidation: 'لائسنس پلیٹ میں 4 ہندسے اور 3 حروف ہونے چاہئیں',
  driverPhoneRequiredPlaceholder: 'ڈرائیور فون (ضروری) مثلاً 512345678',
  whatsappOptionalPlaceholder: 'واٹس ایپ نمبر (اختیاری) مثلاً 512345678',
  trailer: 'ٹریلر',

  // Driver section
  driver: 'ڈرائیور',
  searchOrCreateDriver: 'ڈرائیور تلاش یا بنائیں...',
  createNewDriver: 'نیا ڈرائیور بنائیں',
  driverName: 'ڈرائیور کا نام',
  phoneNumber: 'فون نمبر',
  driverId: 'ڈرائیور کی شناخت',

  // Vehicle section
  vehicle: 'گاڑی',
  searchOrCreateVehicle: 'گاڑی تلاش یا بنائیں...',
  createNewVehicle: 'نئی گاڑی بنائیں',

  // Actions
  addAsset: 'اثاثہ شامل کریں',
  editAsset: 'اثاثہ میں ترمیم کریں',
  deleteAsset: 'اثاثہ حذف کریں',

  // Form section
  form: {
    name: 'اثاثے کا نام',
    namePlaceholder: 'اثاثے کا نام درج کریں',
    status: 'حیثیت',
    validation: {
      nameRequired: 'اثاثے کا نام ضروری ہے',
    },
  },

  // Status
  status: {
    active: 'فعال',
    inactive: 'غیر فعال',
  },

  // Toast messages
  messages: {
    createSuccess: 'اثاثہ کامیابی سے بنایا گیا',
    updateSuccess: 'اثاثہ کامیابی سے اپڈیٹ ہوا',
    deleteSuccess: 'اثاثہ کامیابی سے حذف ہوا',
    deleteConfirm: 'کیا آپ واقعی اس اثاثے کو حذف کرنا چاہتے ہیں؟',
  },

  // Common actions (for compatibility with shared components)
  common: {
    save: 'محفوظ کریں',
    cancel: 'منسوخ کریں',
    create: 'بنائیں',
    edit: 'ترمیم کریں',
    delete: 'حذف کریں',
    close: 'بند کریں',
    back: 'واپس',
    next: 'اگلا',
    submit: 'جمع کرائیں',
  },

  // Table headers
  table: {
    name: 'نام',
    status: 'حیثیت',
  },

  // Fleet Management Section
  fleet: {
    // Common
    title: 'فلیٹ مینجمنٹ',
    subtitle: 'ڈرائیورز، گاڑیوں اور وسائل کی تفویض کا انتظام کریں',
    error: 'خرابی',
    assetNumber: 'اثاثہ نمبر',
    save: 'محفوظ کریں',
    saving: 'محفوظ کیا جا رہا ہے...',
    cancel: 'منسوخ کریں',
    assignments: 'تفویضات',
    actions: 'اقدامات',
    details: 'تفصیلات',
    optional: 'اختیاری',
    delete: 'حذف کریں',
    confirmDelete: 'کیا آپ واقعی اس آئٹم کو حذف کرنا چاہتے ہیں؟',

    // Tabs
    resources: 'وسائل',
    drivers: 'ڈرائیورز',
    vehicles: 'گاڑیاں',
    trailers: 'ٹریلرز',
    resourcesSubtitle: 'ڈرائیور اور گاڑی کی تفویض',
    driversSubtitle: 'اپنے ڈرائیوروں کا انتظام کریں',
    vehiclesSubtitle: 'اپنے گاڑیوں کے بیڑے کا انتظام کریں',
    trailersSubtitle: 'اپنے ٹریلروں کا انتظام کریں',
    search: 'تلاش کریں...',

    // Resources
    addResource: 'وسیلہ تفویض کریں',
    editResource: 'تفویض میں ترمیم کریں',
    resourceName: 'وسیلے کا نام',
    noResources: 'کوئی وسائل کی تفویض نہیں ملی',
    noResourcesDescription: 'اپنی پہلی وسائل کی تفویض بنا کر شروع کریں۔',
    assignDriver: 'ڈرائیور تفویض کریں',
    assignVehicle: 'گاڑی تفویض کریں',
    assignTrailer: 'ٹریلر تفویض کریں',
    assignedSince: 'تفویض کی تاریخ',
    currentShipment: 'موجودہ شپمنٹ',
    resourceStatus: 'حیثیت',
    resourceActive: 'فعال',
    resourceOnDelivery: 'ڈیلیوری پر',
    resourceReturning: 'واپسی پر',
    resourceInactive: 'غیر فعال',
    unassign: 'تفویض ختم کریں',
    totalResources: 'کل وسائل',
    activeResources: 'فعال وسائل',
    onDelivery: 'ڈیلیوری پر',
    resourceDescription: 'تفصیل',
    resourceInformation: 'وسائل کی معلومات',
    assignment: 'تفویض',
    resourceNamePlaceholderEx: 'یونٹ الفا',
    resourceDescriptionPlaceholder: 'اصل ڈیلیوری یونٹ',
    selectDriver: 'ڈرائیور منتخب کریں',
    selectVehicle: 'گاڑی منتخب کریں',
    activateResource: 'فعال کریں',
    deactivateResource: 'غیر فعال کریں',
    resourceActivated: 'وسیلہ فعال ہو گیا',
    resourceDeactivated: 'وسیلہ غیر فعال ہو گیا',
    resourceActivatedDescription: 'وسیلہ اب فعال اور دستیاب ہے۔',
    resourceDeactivatedDescription: 'وسیلہ غیر فعال کر دیا گیا ہے۔',
    resourceDetails: 'وسیلے کی تفصیلات',
    viewResource: 'وسیلہ دیکھیں',
    resourceCreated: 'وسیلہ بنایا گیا',
    resourceCreatedDescription: 'وسیلہ کامیابی سے بنایا گیا ہے۔',
    resourceUpdated: 'وسیلہ اپ ڈیٹ ہوا',
    resourceUpdatedDescription: 'وسیلہ کامیابی سے اپ ڈیٹ ہوا ہے۔',
    resourceUpdateError: 'وسیلہ اپ ڈیٹ نہیں ہو سکا: documentId موجود نہیں',
    resourceDeleted: 'وسیلہ حذف ہو گیا',
    resourceDeletedDescription: 'وسیلہ کامیابی سے سافٹ ڈیلیٹ ہو گیا ہے۔',
    resourceDeleteError: 'وسیلہ حذف نہیں ہو سکا: documentId موجود نہیں',
    resourceDeleteConfirmTitle: 'وسیلہ حذف کریں؟',
    resourceDeleteConfirmDescription: 'اس سے وسیلہ سافٹ ڈیلیٹ ہو جائے گا اور اسے pending یا hold شپمنٹس سے الگ کر دیا جائے گا۔',

    // Drivers
    addDriver: 'ڈرائیور شامل کریں',
    editDriver: 'ڈرائیور میں ترمیم کریں',
    deleteDriver: 'ڈرائیور حذف کریں',
    noDrivers: 'کوئی ڈرائیور نہیں ملے',
    noDriversDescription: 'فلیٹ میں اپنا پہلا ڈرائیور شامل کر کے شروع کریں۔',
    driverName: 'ڈرائیور کا نام',
    driverId: 'ڈرائیور کی شناخت',
    personalInformation: 'ذاتی معلومات',
    driverNamePlaceholder: 'ڈرائیور کا نام',
    driverPhoneRequiredPlaceholder: 'ڈرائیور فون (ضروری) مثلاً 512345678',
    whatsappOptionalPlaceholder: 'واٹس ایپ نمبر (اختیاری) مثلاً 512345678',
    employeeId: 'ملازم نمبر',
    licenseNumber: 'لائسنس نمبر',
    licenseType: 'لائسنس کی قسم',
    licenseExpiry: 'لائسنس کی میعاد',
    selectDate: 'تاریخ منتخب کریں',
    licenseExpiryRequired: 'لائسنس کی میعاد ضروری ہے۔',
    licenseInformationHeading: 'لائسنس کی معلومات',
    licenseHeavy: 'بھاری گاڑی',
    licenseLight: 'ہلکی گاڑی',
    licenseHazmat: 'خطرناک مواد',
    licenseSpecial: 'خصوصی',
    license: 'لائسنس',
    licenseInformation: 'لائسنس کی معلومات',
    phone: 'فون',
    phoneNumber: 'فون نمبر',
    whatsappNumber: 'واٹس ایپ نمبر',
    nationality: 'قومیت',
    employeeNumber: 'ملازم نمبر',
    driverStatus: 'حیثیت',
    driverAvailable: 'دستیاب',
    driverAssigned: 'تفویض شدہ',
    driverOnLeave: 'چھٹی پر',
    driverInactive: 'غیر فعال',
    totalDrivers: 'کل ڈرائیورز',
    availableDrivers: 'دستیاب',
    assignedDrivers: 'تفویض شدہ',
    photo: 'تصویر',
    driverUpdateError: 'ڈرائیور اپ ڈیٹ نہیں ہو سکا: documentId موجود نہیں',
    preferredLanguage: 'پسندیدہ زبان',
    preferredLanguagePlaceholder: 'زبان منتخب کریں',
    langEn: 'انگریزی',
    langAr: 'عربی',
    langUr: 'اردو',

    // Vehicles
    addVehicle: 'گاڑی شامل کریں',
    editVehicle: 'گاڑی میں ترمیم کریں',
    deleteVehicle: 'گاڑی حذف کریں',
    noVehicles: 'کوئی گاڑیاں نہیں ملیں',
    noVehiclesDescription: 'فلیٹ میں اپنی پہلی گاڑی شامل کر کے شروع کریں۔',
    vehicleType: 'گاڑی کی قسم',
    type: 'قسم',
    typeTruck: 'ٹرک',
    typeMixer: 'مکسر',
    typeTanker: 'ٹینکر',
    typeFlatbed: 'فلیٹ بیڈ',
    typeCrane: 'کرین',
    typeLoader: 'لوڈر',
    typePump: 'پمپ',
    typeEquipment: 'سامان',
    make: 'کمپنی',
    model: 'ماڈل',
    makeModel: 'کمپنی / ماڈل',
    year: 'سال',
    vehicleIdentification: 'گاڑی کی شناخت',
    vehicleDetailsHeading: 'گاڑی کی تفصیلات',
    statusAndMaintenance: 'حالت اور دیکھ بھال',
    makePlaceholder: 'مرسڈیز',
    modelPlaceholder: 'Actros',
    yearPlaceholder: 'YYYY',
    vin: 'VIN',
    plateNumber: 'پلیٹ نمبر',
    licensePlate: 'لائسنس پلیٹ',
    vehicleLicensePlate: 'گاڑی کی لائسنس پلیٹ',
    plateDigitsPlaceholder: '0000',
    plateLettersPlaceholder: 'AAA',
    capacity: 'گنجائش',
    capacityUnit: 'یونٹ',
    tons: 'ٹن',
    cubicMeters: 'm³',
    vehicleStatus: 'حیثیت',
    vehicleAvailable: 'دستیاب',
    vehicleAssigned: 'تفویض شدہ',
    vehicleMaintenance: 'دیکھ بھال',
    vehicleOutOfService: 'سروس سے باہر',
    ownership: 'ملکیت',
    owned: 'ملکیتی',
    leased: 'لیز پر',
    rentedOut: 'کرایے پر دیا',
    outsourced: 'آؤٹ سورس',
    brokered: 'بروکر کے ذریعے',
    shared: 'مشترکہ',
    lastMaintenance: 'آخری دیکھ بھال',
    nextMaintenance: 'اگلی دیکھ بھال',
    configureColumns: 'کالم',
    selectVisibleColumns: 'نظر آنے والے کالم منتخب کریں',
    totalVehicles: 'کل گاڑیاں',
    availableVehicles: 'دستیاب',
    vehicleCreated: 'گاڑی بنائی گئی',
    vehicleCreatedDescription: 'گاڑی کامیابی سے بنائی گئی ہے۔',
    vehicleUpdated: 'گاڑی اپ ڈیٹ ہوئی',
    vehicleUpdatedDescription: 'گاڑی کامیابی سے اپ ڈیٹ ہوئی ہے۔',
    vehicleDeleted: 'گاڑی حذف کی گئی',
    vehicleDeletedDescription: 'گاڑی کامیابی سے حذف کی گئی ہے۔',
    vehicleUpdateError: 'گاڑی اپ ڈیٹ نہیں ہو سکی: documentId موجود نہیں',
    makeMercedes: 'Mercedes',
    makeVolvo: 'Volvo',
    makeScania: 'Scania',
    makeMAN: 'MAN',
    makeDAF: 'DAF',
    makeIveco: 'Iveco',
    makeHino: 'Hino',
    makeIsuzu: 'Isuzu',
    makeMitsubishi: 'Mitsubishi',
    makeRenault: 'Renault',
    makeOther: 'دیگر',

    // Trailers
    trailer: 'ٹریلر',
    addTrailer: 'ٹریلر شامل کریں',
    editTrailer: 'ٹریلر میں ترمیم کریں',
    deleteTrailer: 'ٹریلر حذف کریں',
    noTrailers: 'کوئی ٹریلر نہیں ملے',
    noTrailersDescription: 'فلیٹ میں اپنا پہلا ٹریلر شامل کر کے شروع کریں۔',
    trailerStatus: 'ٹریلر کی حیثیت',
    trailerAvailable: 'دستیاب',
    trailerAssigned: 'تفویض شدہ',
    trailerMaintenance: 'دیکھ بھال',
    trailerOutOfService: 'سروس سے باہر',
    selectStatus: 'حیثیت منتخب کریں',

    // Sort options - Drivers
    sortByName: 'نام',
    sortNameAsc: 'نام (الف سے ی)',
    sortNameDesc: 'نام (ی سے الف)',
    sortByLicenseExpiry: 'لائسنس کی میعاد',
    sortLicenseExpiryAsc: 'لائسنس کی میعاد (پہلے ختم ہونے والے)',
    sortLicenseExpiryDesc: 'لائسنس کی میعاد (بعد میں ختم ہونے والے)',
    sortByStatus: 'حیثیت',
    sortStatusAsc: 'حیثیت (الف سے ی)',
    sortStatusDesc: 'حیثیت (ی سے الف)',
    sortByDateAdded: 'شامل کرنے کی تاریخ',
    sortOldest: 'پرانے پہلے',
    sortNewest: 'نئے پہلے',

    // Sort options - Vehicles
    sortByPlateNumber: 'پلیٹ نمبر',
    sortPlateAsc: 'پلیٹ نمبر (الف سے ی)',
    sortPlateDesc: 'پلیٹ نمبر (ی سے الف)',
    sortByMake: 'کمپنی',
    sortMakeAsc: 'کمپنی (الف سے ی)',
    sortMakeDesc: 'کمپنی (ی سے الف)',
    sortByCapacity: 'گنجائش',
    sortCapacityAsc: 'گنجائش (کم سے زیادہ)',
    sortCapacityDesc: 'گنجائش (زیادہ سے کم)',
    sortByOwnership: 'ملکیت',
    sortOwnershipAsc: 'ملکیت (الف سے ی)',
    sortOwnershipDesc: 'ملکیت (ی سے الف)',

    // Sort options - Resources
    sortByAssignedSince: 'تفویض کی تاریخ',
    sortAssignedOldest: 'پہلے تفویض ہونے والے',
    sortAssignedNewest: 'بعد میں تفویض ہونے والے',

    // Filter Labels
    filterDriverName: 'ڈرائیور کا نام',
    filterDriverPhone: 'فون نمبر',
    filterEmployeeNumber: 'ملازم نمبر',
    filterLicensePlate: 'لائسنس پلیٹ',
    filterBindingStatus: 'تفویض کی حیثیت',
    filterBindingStatusAll: 'سب',
    filterBindingStatusYes: 'تفویض شدہ',
    filterBindingStatusNo: 'غیر تفویض شدہ',
    filterDriverNamePlaceholder: 'ڈرائیور کے نام سے تلاش کریں...',
    filterPhoneNumberPlaceholder: 'فون سے تلاش کریں...',
    filterEmployeeNumberPlaceholder: 'ملازم نمبر سے تلاش کریں...',
    filterLicensePlatePlaceholder: 'لائسنس پلیٹ سے تلاش کریں...',
    filterVehicleLicensePlate: 'گاڑی کی لائسنس پلیٹ',
    searchByName: 'نام سے تلاش کریں',
    searchByPhone: 'فون سے تلاش کریں',
    searchByEmployeeNumber: 'ملازم نمبر سے تلاش کریں',
    searchByLicensePlate: 'لائسنس پلیٹ سے تلاش کریں',
    searchByDriverName: 'ڈرائیور کے نام سے تلاش کریں',
    searchByVehiclePlate: 'گاڑی کی پلیٹ سے تلاش کریں',
    searchByDriverPhone: 'ڈرائیور کے فون سے تلاش کریں',
    enterDriverName: 'ڈرائیور کا نام درج کریں',
    enterPhoneNumber: 'فون نمبر درج کریں',
    enterEmployeeNumber: 'ملازم نمبر درج کریں',
    enterLicensePlate: 'لائسنس پلیٹ درج کریں',
    bindingStatus: 'تفویض کی حیثیت',
    bindedToResource: 'وسیلے سے منسلک',
    notBinded: 'منسلک نہیں',
    driverPhone: 'ڈرائیور فون',

    // Detail view sections
    basicInformation: 'بنیادی معلومات',
    specifications: 'تفصیلات',
    additionalInfo: 'اضافی معلومات',
    description: 'تفصیل',

    deactivationWarning: {
      title: 'آئٹم غیر فعال کر دیا گیا',
      shipmentCount: '{{count}} آرڈر(ز) خود بخود غیر تفویض کیے گئے اور دوبارہ تفویض کی ضرورت ہے',
      shipmentList: 'متاثرہ آرڈرز: {{list}}',
      resourceCount: '{{count}} وسیلہ (وسائل) بھی غیر فعال کر دیے گئے',
      resourceList: 'متاثرہ وسائل: {{list}}',
      viewOrders: 'متاثرہ آرڈرز دیکھیں',
      dismiss: 'برخاست کریں',
    },

    // Toast messages for fleet UI actions
    exportingTitle: 'برآمد ہو رہا ہے...',
    exportingDesc: 'تمام ریکارڈز حاصل کیے جا رہے ہیں',
    exportFailedTitle: 'برآمد ناکام',
    exportDriversFailed: 'ڈرائیورز برآمد نہیں ہو سکے',
    exportVehiclesFailed: 'گاڑیاں برآمد نہیں ہو سکیں',
    exportResourcesFailed: 'وسائل برآمد نہیں ہو سکے',
    createDriverFailed: 'ڈرائیور بنانے میں ناکامی',
    createVehicleFailed: 'گاڑی بنانے میں ناکامی',
    createResourceFailed: 'وسیلہ بنانے میں ناکامی',
    importingTitle: 'درآمد ہو رہا ہے...',
    importingDesc: 'فائل ریکارڈ پروسیس ہو رہے ہیں',
    importSuccess: 'درآمد مکمل',
    importSuccessDesc: '{{created}} بنائے گئے، {{skipped}} چھوڑے گئے',
    importFailed: 'درآمد ناکام',
    importDriversFailed: 'ڈرائیور درآمد نہیں ہو سکے',
    importVehiclesFailed: 'گاڑیاں درآمد نہیں ہو سکیں',
    importResourcesFailed: 'اسائنمنٹس درآمد نہیں ہو سکے',
    importNoRows: 'فائل میں کوئی درست قطار نہیں',
    importSelectTenantFirst: 'پہلے کرایہ دار منتخب کریں',
    importSelectTenantFirstDescription: 'ڈیٹا درآمد کرنے سے پہلے اوپر کی بار سے ایک مخصوص کرایہ دار منتخب کریں۔',
    importDriversLabel: 'ڈرائیور اپلوڈ کریں',
    importVehiclesLabel: 'گاڑیاں اپلوڈ کریں',
    importResourcesLabel: 'اسائنمنٹس اپلوڈ کریں',
  },

  // Countries
  countries: {
    saudiArabia: 'سعودی عرب',
    uae: 'متحدہ عرب امارات',
    egypt: 'مصر',
    pakistan: 'پاکستان',
    india: 'بھارت',
    bangladesh: 'بنگلہ دیش',
    philippines: 'فلپائن',
    nepal: 'نیپال',
    jordan: 'اردن',
    syria: 'شام',
    yemen: 'یمن',
    sudan: 'سوڈان',
    other: 'دیگر',
  },

  // Top-level convenience keys (flat versions for direct access)
  name: 'نام',
  namePlaceholder: 'اثاثہ کا نام درج کریں',
  statusLabel: 'حالت',
  nameRequired: 'اثاثہ کا نام ضروری ہے',
  inactive: 'غیر فعال',
  createSuccess: 'اثاثہ کامیابی سے بنایا گیا',
  updateSuccess: 'اثاثہ کامیابی سے اپ ڈیٹ ہوا',
  deleteSuccess: 'اثاثہ کامیابی سے حذف ہوا',
  deleteConfirm: 'کیا آپ واقعی اس اثاثے کو حذف کرنا چاہتے ہیں؟',
  actions: 'اقدامات',
  details: 'تفصیلات',
  optional: 'اختیاری',
  delete: 'حذف کریں',
  confirmDelete: 'کیا آپ واقعی اس آئٹم کو حذف کرنا چاہتے ہیں؟',
  resources: 'وسائل',
  drivers: 'ڈرائیورز',
  vehicles: 'گاڑیاں',
  trailers: 'ٹریلرز',
  resourcesSubtitle: 'ڈرائیور اور گاڑی کی تفویض',
  driversSubtitle: 'اپنے ڈرائیوروں کا انتظام کریں',
  vehiclesSubtitle: 'اپنے گاڑیوں کے بیڑے کا انتظام کریں',
  trailersSubtitle: 'اپنے ٹریلروں کا انتظام کریں',
  search: 'تلاش کریں...',
  addResource: 'وسیلہ تفویض کریں',
  editResource: 'تفویض میں ترمیم کریں',
  noResources: 'کوئی وسائل کی تفویض نہیں ملی',
  noResourcesDescription: 'اپنی پہلی وسائل کی تفویض بنا کر شروع کریں۔',
  assignDriver: 'ڈرائیور تفویض کریں',
  assignVehicle: 'گاڑی تفویض کریں',
  assignTrailer: 'ٹریلر تفویض کریں',
  assignedSince: 'تفویض کی تاریخ',
  currentShipment: 'موجودہ شپمنٹ',
  resourceStatus: 'حالت',
  resourceActive: 'فعال',
  resourceOnDelivery: 'ترسیل پر',
  resourceReturning: 'واپسی پر',
  resourceInactive: 'غیر فعال',
  unassign: 'تفویض ختم کریں',
  totalResources: 'کل وسائل',
  activeResources: 'فعال وسائل',
  onDelivery: 'ترسیل پر',
  resourceDescription: 'تفصیل',
  activateResource: 'فعال کریں',
  deactivateResource: 'غیر فعال کریں',
  resourceActivated: 'وسیلہ فعال ہو گیا',
  resourceDeactivated: 'وسیلہ غیر فعال ہو گیا',
  resourceActivatedDescription: 'وسیلہ اب فعال اور دستیاب ہے۔',
  resourceDeactivatedDescription: 'وسیلہ غیر فعال کر دیا گیا ہے۔',
  resourceDetails: 'وسیلے کی تفصیلات',
  viewResource: 'وسیلہ دیکھیں',
  addDriver: 'ڈرائیور شامل کریں',
  editDriver: 'ڈرائیور میں ترمیم کریں',
  deleteDriver: 'ڈرائیور حذف کریں',
  noDrivers: 'کوئی ڈرائیور نہیں ملے',
  noDriversDescription: 'فلیٹ میں اپنا پہلا ڈرائیور شامل کر کے شروع کریں۔',
  driverNameAr: 'ڈرائیور کا نام (عربی)',
  employeeId: 'ملازم نمبر',
  licenseNumber: 'لائسنس نمبر',
  licenseType: 'لائسنس کی قسم',
  licenseExpiry: 'لائسنس کی میعاد',
  selectDate: 'تاریخ منتخب کریں',
  licenseExpiryRequired: 'لائسنس کی میعاد ضروری ہے۔',
  licenseHeavy: 'بھاری گاڑی',
  licenseLight: 'ہلکی گاڑی',
  licenseHazmat: 'خطرناک مواد',
  licenseSpecial: 'خصوصی',
  phone: 'فون',
  whatsappNumber: 'واٹس ایپ نمبر',
  driverStatus: 'حالت',
  driverAvailable: 'دستیاب',
  driverAssigned: 'تفویض شدہ',
  driverOnLeave: 'چھٹی پر',
  driverInactive: 'غیر فعال',
  totalDrivers: 'کل ڈرائیورز',
  availableDrivers: 'دستیاب',
  assignedDrivers: 'تفویض شدہ',
  photo: 'تصویر',
  addVehicle: 'گاڑی شامل کریں',
  editVehicle: 'گاڑی میں ترمیم کریں',
  deleteVehicle: 'گاڑی حذف کریں',
  noVehicles: 'کوئی گاڑی نہیں ملی',
  noVehiclesDescription: 'فلیٹ میں اپنی پہلی گاڑی شامل کر کے شروع کریں۔',
  typeTruck: 'ٹرک',
  typeMixer: 'مکسر',
  typeTanker: 'ٹینکر',
  typeFlatbed: 'فلیٹ بیڈ',
  typeCrane: 'کرین',
  typeLoader: 'لوڈر',
  typePump: 'پمپ',
  typeEquipment: 'آلات',
  make: 'کمپنی',
  model: 'ماڈل',
  makeModel: 'کمپنی / ماڈل',
  year: 'سال',
  vin: 'VIN',
  capacityUnit: 'یونٹ',
  tons: 'ٹن',
  cubicMeters: 'm³',
  vehicleStatus: 'حالت',
  vehicleAvailable: 'دستیاب',
  vehicleAssigned: 'تفویض شدہ',
  vehicleMaintenance: 'مرمت',
  vehicleOutOfService: 'سروس سے باہر',
  ownership: 'ملکیت',
  rentedOut: 'کرائے پر دی گئی',
  outsourced: 'بیرونی',
  brokered: 'بروکر',
  shared: 'مشترکہ',
  lastMaintenance: 'آخری مرمت',
  nextMaintenance: 'اگلی مرمت',
  configureColumns: 'کالم',
  selectVisibleColumns: 'نظر آنے والے کالم منتخب کریں',
  totalVehicles: 'کل گاڑیاں',
  availableVehicles: 'دستیاب',
  addTrailer: 'ٹریلر شامل کریں',
  editTrailer: 'ٹریلر میں ترمیم کریں',
  deleteTrailer: 'ٹریلر حذف کریں',
  noTrailers: 'کوئی ٹریلر نہیں ملے',
  noTrailersDescription: 'فلیٹ میں اپنا پہلا ٹریلر شامل کر کے شروع کریں۔',
  sortByName: 'نام',
  sortNameAsc: 'نام (الف-ی)',
  sortNameDesc: 'نام (ی-الف)',
  sortByLicenseExpiry: 'لائسنس کی میعاد',
  sortLicenseExpiryAsc: 'لائسنس کی میعاد (پہلے قریب ترین)',
  sortLicenseExpiryDesc: 'لائسنس کی میعاد (پہلے دور ترین)',
  sortByStatus: 'حالت',
  sortStatusAsc: 'حالت (الف-ی)',
  sortStatusDesc: 'حالت (ی-الف)',
  sortByDateAdded: 'شامل کی تاریخ',
  sortOldest: 'پرانے پہلے',
  sortNewest: 'نئے پہلے',
  sortByPlateNumber: 'پلیٹ نمبر',
  sortPlateAsc: 'پلیٹ نمبر (الف-ی)',
  sortPlateDesc: 'پلیٹ نمبر (ی-الف)',
  sortByMake: 'کمپنی',
  sortMakeAsc: 'کمپنی (الف-ی)',
  sortMakeDesc: 'کمپنی (ی-الف)',
  sortByCapacity: 'گنجائش',
  sortCapacityAsc: 'گنجائش (کم سے زیادہ)',
  sortCapacityDesc: 'گنجائش (زیادہ سے کم)',
  sortByOwnership: 'ملکیت',
  sortOwnershipAsc: 'ملکیت (الف-ی)',
  sortOwnershipDesc: 'ملکیت (ی-الف)',
  sortByAssignedSince: 'تفویض کی تاریخ',
  sortAssignedOldest: 'پہلے تفویض شدہ پہلے',
  sortAssignedNewest: 'بعد میں تفویض شدہ پہلے',
  filterDriverName: 'ڈرائیور کا نام',
  filterDriverPhone: 'فون نمبر',
  filterEmployeeNumber: 'ملازم نمبر',
  filterLicensePlate: 'لائسنس پلیٹ',
  filterBindingStatus: 'بائنڈنگ کی حالت',
  filterBindingStatusAll: 'تمام',
  filterBindingStatusYes: 'تفویض شدہ',
  filterBindingStatusNo: 'غیر تفویض شدہ',
  filterDriverNamePlaceholder: 'ڈرائیور کے نام سے تلاش کریں...',
  filterPhoneNumberPlaceholder: 'فون سے تلاش کریں...',
  filterEmployeeNumberPlaceholder: 'ملازم نمبر سے تلاش کریں...',
  filterLicensePlatePlaceholder: 'لائسنس پلیٹ سے تلاش کریں...',
  filterVehicleLicensePlate: 'گاڑی کی لائسنس پلیٹ',
  saudiArabia: 'سعودی عرب',
  uae: 'متحدہ عرب امارات',
  egypt: 'مصر',
  pakistan: 'پاکستان',
  india: 'بھارت',
  bangladesh: 'بنگلہ دیش',
  philippines: 'فلپائن',
  nepal: 'نیپال',
  jordan: 'اردن',
  syria: 'شام',
  yemen: 'یمن',
  sudan: 'سوڈان',
  other: 'دیگر',

  // Section headings
  vehicleIdentification: 'گاڑی کی شناخت',
  vehicleDetailsHeading: 'گاڑی کی تفصیلات',
  statusAndMaintenance: 'حالت اور دیکھ بھال',
  personalInformation: 'ذاتی معلومات',
  licenseInformationHeading: 'لائسنس کی معلومات',
  trailerInformation: 'ٹریلر کی معلومات',
  resourceInformation: 'وسائل کی معلومات',
  assignment: 'تفویض',

  // Placeholders
  makePlaceholder: 'مرسڈیز',
  modelPlaceholder: 'Actros',
  yearPlaceholder: 'YYYY',
  driverNamePlaceholder: 'نام',
  licensePlaceholder: 'DL-12345',
  trailerMakePlaceholder: 'برانڈ درج کریں',
  trailerModelPlaceholder: 'ماڈل درج کریں',
  vinPlaceholder: 'VIN درج کریں',
  descriptionPlaceholder: 'تفصیل درج کریں',
  resourceNamePlaceholderEx: 'یونٹ الفا',
  resourceDescriptionPlaceholder: 'اصل ڈیلیوری یونٹ',
  plateDigitsPlaceholder: '0000',
  plateLettersPlaceholder: 'AAA',
  trailerIdPlaceholder: 'TRL-001',
  trailerPlatePlaceholder: 'ABC-123',
  trailerYearPlaceholder: '2024',
  licensePlateExamplePlaceholder: 'مثلاً: 5080-ZZR',

  // Select options
  selectDriver: 'ڈرائیور منتخب کریں',
  selectVehicle: 'گاڑی منتخب کریں',

  errors: {
    driverNotFound: 'ڈرائیور نہیں ملا',
    vehicleNotFound: 'گاڑی نہیں ملی',
    trailerNotFound: 'ٹریلر نہیں ملا',
    resourceNotFound: 'وسیلہ نہیں ملا',
  },
}
